/**
 * Single shared QueryClient for the whole CRM.
 *
 * Defaults are tuned for an admin/CRM workload:
 * - 30s staleTime: lists feel snappy, no stampede on tab switches
 * - 5 min cacheTime (gcTime): keep data warm during quick navigation
 * - retry once on transient errors; 4xx are not retried
 * - refetchOnWindowFocus disabled to match existing UX (no surprise reloads)
 */
import { QueryClient } from '@tanstack/react-query';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000,
      gcTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        const status = error?.response?.status;
        if (status && status >= 400 && status < 500) return false;
        return failureCount < 1;
      },
    },
    mutations: {
      retry: 0,
    },
  },
});

// Centralized cache-key namespaces. Keep these stable; mutations rely on them
// for invalidation via prefix matching (e.g. ['clients'] invalidates list+detail).
export const queryKeys = {
  clients: {
    all: ['clients'],
    list: (params) => ['clients', 'list', params || {}],
    detail: (id) => ['clients', 'detail', id],
  },
  applications: {
    all: ['applications'],
    list: (params) => ['applications', 'list', params || {}],
    detail: (id) => ['applications', 'detail', id],
  },
  notifications: {
    all: ['notifications'],
    list: (params) => ['notifications', 'list', params || {}],
    unreadCount: ['notifications', 'unread-count'],
  },
  introducers: {
    all: ['introducers'],
    list: (params) => ['introducers', 'list', params || {}],
    detail: (id) => ['introducers', 'detail', id],
  },
  lenders: {
    all: ['lenders'],
    catalogue: (category) => ['lenders', 'catalogue', category || 'all'],
  },
};
